import { SortingState } from "@tanstack/react-table";
import {
  GET_LIST_REQUEST_DEFAULT_LIMIT,
  PRODUCTS_PAGE_SEARCH_PARAMETER
} from "constants/common";
import { useManageTable } from "hooks";
import { CategoryTypes } from "modules/categories";
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useGetProductsByFilter } from "../apis/useGetProductsByFilter";
import { GetProductsByFilterBody } from "../apis/useGetProductsByFilter/getProductsByFilter.types";
import {
  COLORS_FILTER_SEARCH_PARAMETER,
  MAX_PRICE_FILTER_SEARCH_PARAMETER,
  MIN_PRICE_FILTER_SEARCH_PARAMETER,
  SIZES_FILTER_SEARCH_PARAMETER,
  STATUS_FILTER_SEARCH_PARAMETER
} from "../constants";
import { ProductStatusFilterValues } from "../product.types";
import useProductsFilter from "./useProductsFilter";

interface DefaultParams {
  defaultSorting?: SortingState;
  defaultTake?: number;
  excludeVariantIds?: string[];
}

const useProductsList = (defaultParams?: DefaultParams) => {
  const { state } = useLocation();
  const {
    page,
    searchValue,
    debouncedSearch,
    sorting,
    setSorting,
    setSearchValue,
    setPage
  } = useManageTable({
    pageSearchParameter: PRODUCTS_PAGE_SEARCH_PARAMETER,
    defaultSorting:
      state?.sortBy && state?.sortOrder
        ? [{ id: state.sortBy, desc: state.sortOrder === "desc" }]
        : (defaultParams?.defaultSorting ?? [])
  });

  const { categories, filterMeta, isFilterMetaLoading, isCategoriesLoading } =
    useProductsFilter();

  const [searchParams] = useSearchParams();
  const selectedStatus = searchParams.get(STATUS_FILTER_SEARCH_PARAMETER);
  const selectedCategory = searchParams.get(CategoryTypes.MAIN_CATEGORY);
  const selectedSubCategory = searchParams.get(CategoryTypes.SUB_CATEGORY);
  const selectedSubChildCategory = searchParams.get(
    CategoryTypes.SUB_CHILD_CATEGORY
  );
  const selectedSizes = searchParams.getAll(SIZES_FILTER_SEARCH_PARAMETER);
  const selectedColors = searchParams.getAll(COLORS_FILTER_SEARCH_PARAMETER);
  const minPrice = searchParams.get(MIN_PRICE_FILTER_SEARCH_PARAMETER);
  const maxPrice = searchParams.get(MAX_PRICE_FILTER_SEARCH_PARAMETER);
  const attributeValuesIdsGroups = filterMeta?.attributes
    .map((attribute) => {
      const selectedAttributeValues = searchParams.getAll(String(attribute.id));

      return selectedAttributeValues;
    })
    .filter((group) => group.length > 0);

  const requestBody: GetProductsByFilterBody = useMemo(
    () => ({
      page,
      take: defaultParams?.defaultTake ?? GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch,
      orderBy: sorting[0]?.id,
      order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "DESC",
      isVerified:
        !selectedStatus || selectedStatus === ProductStatusFilterValues.ALL
          ? undefined
          : selectedStatus === ProductStatusFilterValues.VERIFIED,
      categoryId:
        selectedSubChildCategory ?? selectedSubCategory ?? selectedCategory,
      sizesIds: selectedSizes ?? [],
      colorValues: selectedColors,
      minPrice: minPrice ? +minPrice : undefined,
      maxPrice: maxPrice ? +maxPrice : undefined,
      attributeValuesIdsGroups,
      excludeVariantIds: defaultParams?.excludeVariantIds
    }),
    [
      page,
      debouncedSearch,
      sorting,
      selectedSubChildCategory,
      selectedSubCategory,
      selectedCategory,
      selectedColors,
      minPrice,
      maxPrice,
      defaultParams?.excludeVariantIds
    ]
  );

  const {
    products,
    meta,
    isLoading,
    refetch: refetchProducts
  } = useGetProductsByFilter(requestBody);

  useEffect(() => {
    if (meta && meta.page > meta.pageCount) {
      setPage(meta.pageCount || 1);
    }
  }, [meta]);

  return {
    products,
    totalItems: meta?.itemCount ?? 0,
    searchValue,
    page,
    isLoading,
    sorting,
    setSorting,
    setSearchValue,
    setPage,
    refetchProducts,
    categories,
    filterMeta,
    isCategoriesLoading,
    isFilterMetaLoading
  };
};

export default useProductsList;
